// src/forms/html_element_helpers/error_tracking.js

import { fetch_with_timeout } from '../../helpers';
import merge from 'lodash.merge';

const default_maximum_error_tracking_requests_per_page = 3;

const initialize_rate_limited_error_tracking_state_on_data_hub_instance = (
	data_hub_instance,
	maximum_error_tracking_requests_per_page = default_maximum_error_tracking_requests_per_page
) => {
	if (!data_hub_instance || typeof data_hub_instance !== 'object') {
		return null;
	}

	if (
		typeof data_hub_instance.maximum_error_tracking_requests_per_page !== 'number' ||
		Number.isNaN(data_hub_instance.maximum_error_tracking_requests_per_page)
	) {
		data_hub_instance.maximum_error_tracking_requests_per_page = maximum_error_tracking_requests_per_page;
	}

	if (
		typeof data_hub_instance.error_tracking_request_count !== 'number' ||
		Number.isNaN(data_hub_instance.error_tracking_request_count)
	) {
		data_hub_instance.error_tracking_request_count = 0;
	}

	if (typeof data_hub_instance.is_error_tracking_request_in_flight !== 'boolean') {
		data_hub_instance.is_error_tracking_request_in_flight = false;
	}

	return data_hub_instance;
};

/**
 * Tracks errors by POSTing to the action endpoint with a single data_hub_events error event.
 * 
 * @param {Object} params - Named parameters.
 * @param {string} params.internal_error_message - Internal error message for debugging.
 * @param {string} params.external_error_message - User-facing error message.
 * @param {Object} [params.data_hub_instance=window?.data_hub] - The data_hub instance.
 * @param {Object} [params.user_data={}] - Current user data.
 * @param {Object} [params.metadata={}] - Merged metadata (page + form).
 * @param {Object} [params.custom_error_data={}] - Additional error-specific data.
 * @param {Function} [params.error_function] - Error callback for tracking failures (optional, won't throw).
 * 
 * @returns {Promise<void>} Promise that resolves when error tracking is complete (or fails silently).
 */
export async function track_error_to_action_endpoint({
	internal_error_message,
	external_error_message,
	data_hub_instance = window?.data_hub,
	user_data = {},
	metadata = {},
	custom_error_data = {},
	error_function = null
}) {
	// Don't track if no data_hub_instance or forms_domain
	if (!data_hub_instance || !data_hub_instance.forms_domain) {
		if (error_function) {
			error_function?.('Error tracking skipped: no data_hub_instance or forms_domain', null);
		}
		return;
	}

	try {
		// Create a single error event for logging
		const error_events = [
			{
				verb: 'data_hub_events',
				data: {
					event_name: 'error',
					source: 'data_hub_form',
					custom_data: {
						...custom_error_data,
						internal_error_message: internal_error_message,
						external_error_message: external_error_message,
						error_timestamp: new Date().toISOString()
					}
				}
			}
		];

		// Merge user_data from data_hub_instance if available
		const merged_user_data = merge(
			{},
			data_hub_instance?.user_data || {},
			window?.data_hub_user_data || {},
			user_data
		);

		// Merge metadata
		const merged_metadata = merge(
			{},
			data_hub_instance?.page_metadata || {},
			metadata
		);

		// Build the action object to send
		const error_action_object = {
			events: error_events,
			returns: {
				user_data: '{{user_data}}',
				metadata: '{{metadata}}'
			},
			user_data: merged_user_data,
			metadata: merged_metadata
		};

		// Add JWT if available
		if (data_hub_instance.jwt_cookie) {
			error_action_object.jwt = data_hub_instance.jwt_cookie;
		}

		// Build the API URL
		const error_tracking_api_url = `${data_hub_instance.forms_domain}/api/post/user_data`;

		// Create headers
		const headers = {
			'Content-Type': 'application/json'
		};

		// POST to action endpoint (fire and forget - don't block on errors)
		const response = await fetch_with_timeout(error_tracking_api_url, {
			method: 'POST',
			headers: headers,
			credentials: 'include',
			body: JSON.stringify(error_action_object),
			timeout: 5000
		}).catch(error => {
			// Silently fail - we don't want error tracking to cause additional errors
			if (error_function) {
				error_function?.('Error tracking POST failed (non-blocking): ' + error, null);
			}
			return null;
		});

		// Return parsed response (caller can read jwt_cookie for unknown_uuid correlation)
		if (response?.ok) {
			try {
				return await response.json();
			} catch (_) {
				return null;
			}
		}

	} catch (error) {
		// Silently fail - we don't want error tracking to cause additional errors
		if (error_function) {
			error_function?.('Error in track_error_to_action_endpoint (non-blocking): ' + error, null);
		}
	}
}

/**
 * Logs an error to the console and tracks it to the action endpoint with per-instance rate limiting.
 *
 * @param {Object} params
 * @param {string} params.internal_error_message
 * @param {string} params.external_error_message
 * @param {Object} [params.data_hub_instance=window?.data_hub]
 * @param {Object} [params.user_data]
 * @param {Object} [params.metadata]
 * @param {Object} [params.custom_error_data]
 * @param {number} [params.maximum_error_tracking_requests_per_page=3]
 * @returns {Promise<Object|null>}
 */
export async function log_console_error_and_track_to_action_endpoint_with_rate_limit({
	internal_error_message = null,
	external_error_message = null,
	data_hub_instance = window?.data_hub,
	user_data = data_hub_instance?.user_data || {},
	metadata = data_hub_instance?.page_metadata || {},
	custom_error_data = {},
	maximum_error_tracking_requests_per_page = default_maximum_error_tracking_requests_per_page
} = {}) {
	console.error(internal_error_message, external_error_message);

	const initialized_data_hub_instance = initialize_rate_limited_error_tracking_state_on_data_hub_instance(
		data_hub_instance,
		maximum_error_tracking_requests_per_page
	);

	if (
		!initialized_data_hub_instance ||
		!initialized_data_hub_instance.forms_domain ||
		initialized_data_hub_instance.error_tracking_request_count >= initialized_data_hub_instance.maximum_error_tracking_requests_per_page ||
		initialized_data_hub_instance.is_error_tracking_request_in_flight
	) {
		return null;
	}

	initialized_data_hub_instance.error_tracking_request_count += 1;
	initialized_data_hub_instance.is_error_tracking_request_in_flight = true;

	return track_error_to_action_endpoint({
		internal_error_message,
		external_error_message,
		data_hub_instance: initialized_data_hub_instance,
		user_data,
		metadata,
		custom_error_data,
		error_function: null
	}).then((response_data) => {
		if (response_data?.jwt_cookie && !initialized_data_hub_instance.jwt_cookie) {
			initialized_data_hub_instance.jwt_cookie = response_data.jwt_cookie;
		}
		return response_data;
	}).catch(() => {
		return null;
	}).finally(() => {
		initialized_data_hub_instance.is_error_tracking_request_in_flight = false;
	});
}

